/**
 * Importing a file as a Draft article (piece 30, Importing a file; T339;
 * decision 0953), and the value type the `DocumentTextProvider` seam answers
 * (piece 2).
 *
 * 🔨 A reader answers typed blocks, never markup: the article's stored body is
 * written here from the blocks and then run through the article's own
 * sanitiser, so an import stores nothing the editor could not have written.
 */
import { SAFE_HREF, escapeHtml } from '../controls/richText';
import { ARTICLE_PICTURE_CAPTION_ATTRIBUTE, ARTICLE_PICTURE_CAPTION_MAX, sanitizeArticleBody } from './body';
import { fill, t, type StringKey } from '../strings';
import { ARTICLE_PICTURE_MAX_BYTES, isArticlePictureMime } from './pictures';
import { articleCalloutHtml, type ArticleCalloutKind } from './callouts';

/** The kinds the import reads; a DOC only through a converter to a DOCX, and a Markdown file as the fifth (T396; decision 1102). */
export const ARTICLE_IMPORT_KINDS = ['pdf', 'docx', 'doc', 'txt', 'html', 'md'] as const;
export type ArticleImportKind = (typeof ARTICLE_IMPORT_KINDS)[number];

/** Each file-name extension the import knows, and the kind it names. */
export const ARTICLE_IMPORT_EXTENSIONS: Readonly<Record<string, ArticleImportKind>> = {
  pdf: 'pdf', docx: 'docx', doc: 'doc', txt: 'txt', text: 'txt', html: 'html', htm: 'html', md: 'md', markdown: 'md',
};

/** What the window's file picker offers. */
export const ARTICLE_IMPORT_ACCEPT = '.pdf,.docx,.doc,.txt,.html,.htm,.md,.markdown';

/**
 * 🔒 The six reasons a file is refused, each with its own sentence (decision
 * 0953): another kind of file, a DOC with no converter, a scan, a PDF locked
 * with a password, a file holding no words and a damaged one.
 */
export const ARTICLE_IMPORT_REFUSALS = ['kind', 'legacyDoc', 'scan', 'locked', 'empty', 'unreadable'] as const;
export type ArticleImportRefusal = (typeof ARTICLE_IMPORT_REFUSALS)[number];

export function isArticleImportRefusal(value: unknown): value is ArticleImportRefusal {
  return typeof value === 'string' && (ARTICLE_IMPORT_REFUSALS as readonly string[]).includes(value);
}

/** The lookup key of a refusal's sentence, which the window and the API both read (the strings live with the knowledge base's). */
export function articleImportRefusalKey(reason: ArticleImportRefusal): `kb.import.refused.${ArticleImportRefusal}` {
  return `kb.import.refused.${reason}`;
}

/** One run of words and how it is marked. A `\n` inside `text` is a line break. */
export interface ImportRun {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
  /** 🔒 A monospace run, kept as inline code (T3571; decision 10511). */
  code?: boolean;
  /** A web, mail or telephone address; any other address is dropped and the words kept. */
  href?: string | null;
}

/**
 * Why a picture was left out: not one of the four rasters, over five
 * megabytes, only an address on the internet, or a path to a file beside the
 * page or the Markdown file, which the import never receives (T396; decision
 * 1102; T856).
 */
export type ImportPictureLeftOut = 'type' | 'size' | 'remote' | 'relative';

/** 🔒 The alignment of a picture's own line, where the file centered or right-aligned it (T3571; decision 10511). */
export type ImportPictureAlign = 'center' | 'right';

/** The letters a lettered list keeps, lower or upper case (decision 1011). */
export type ImportListStyle = 'a' | 'A';

export interface ImportListItem {
  runs: ImportRun[];
  /** A list inside the item, or a picture it holds. */
  children: ImportBlock[];
}

export type ImportBlock =
  | { kind: 'heading'; level: 1 | 2 | 3; runs: ImportRun[] }
  | { kind: 'paragraph'; runs: ImportRun[] }
  /** A numbered list may open past 1, and a lettered one keeps its letters (decision 1011). */
  | { kind: 'list'; ordered: boolean; items: ImportListItem[]; start?: number; style?: ImportListStyle }
  | { kind: 'table'; headerRow: boolean; rows: ImportRun[][][] }
  /** A picture kept names its index among the reading's pictures; one left out names why and what it was. */
  | { kind: 'picture'; picture: number | null; alt: string; leftOut?: ImportPictureLeftOut; name?: string; align?: ImportPictureAlign; caption?: string }
  | { kind: 'quote'; runs: ImportRun[] }
  | { kind: 'code'; text: string }
  | { kind: 'rule' }
  /** 🔒 A Note, Warning or Tip callout holding the words after its opening word (T3572; decision 10530). */
  | { kind: 'callout'; callout: ArticleCalloutKind; runs: ImportRun[] };

/**
 * 🔒 A Word tab as spacing the article can show (T375; decision 1013): a tab
 * opening a line is a fixed indent of eight non-breaking spaces, and a tab
 * between words a wide space of four. The article's table keeps a non-breaking
 * space, so neither needs anything the table does not already admit.
 */
export const IMPORT_TAB_INDENT = '\u00a0'.repeat(8);
export const IMPORT_TAB_SPACE = '\u00a0'.repeat(4);

/** A picture the reader found inside the file, one of the four rasters an article keeps. */
export interface ImportPicture {
  bytes: Uint8Array;
  mimeType: string;
  alt: string;
  /** A name for the picture's File. */
  filename: string;
}

/** 🔒 What the `DocumentTextProvider` seam answers for one file (piece 2; decision 0953): read, or refused with its reason. */
export type DocumentTextReading =
  /**
   * `linesLeftOut` counts the running header and footer lines a PDF's reading left out (T371; decision 1012), and
   * `questions` are what the reader was unsure of, each read with the answer given or its pre-selected tile (T386; decision 1014).
   */
  | { status: 'read'; kind: ArticleImportKind; title: string | null; blocks: ImportBlock[]; pictures: ImportPicture[]; linesLeftOut: number; questions: ImportQuestion[] }
  | { status: 'refused'; kind: ArticleImportKind | null; reason: ArticleImportRefusal };

const SIGNATURE_PDF = [0x25, 0x50, 0x44, 0x46, 0x2d];
const SIGNATURE_ZIP = [0x50, 0x4b, 0x03, 0x04];
const SIGNATURE_OLE = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1];

function startsWith(head: Uint8Array, signature: readonly number[]): boolean {
  return signature.every((byte, index) => head[index] === byte);
}

/** Whether the bytes open as a compound file, which is what Word writes for a DOC and for a DOCX locked with a password. */
export function isCompoundFile(head: Uint8Array): boolean {
  return startsWith(head, SIGNATURE_OLE);
}

/**
 * 🔒 The kind a file is (decision 0953): read from its name and its opening
 * bytes, never from the media type a browser claims. A PDF's bytes name it
 * whatever it is called, a DOC that is really a ZIP is a DOCX, and a file with
 * no known extension is sniffed for a PDF or a web page. Null is another kind.
 */
export function articleImportKind(filename: string, head: Uint8Array): ArticleImportKind | null {
  if (startsWith(head, SIGNATURE_PDF)) return 'pdf';
  const dot = filename.lastIndexOf('.');
  const extension = dot >= 0 ? filename.slice(dot + 1).toLowerCase() : '';
  const named = ARTICLE_IMPORT_EXTENSIONS[extension] ?? null;
  if (named === 'doc' && startsWith(head, SIGNATURE_ZIP)) return 'docx';
  // 🔒 A Markdown file is text: one opening as a ZIP or a compound file is some other kind under a Markdown name (decision 1102).
  if (named === 'md' && (startsWith(head, SIGNATURE_ZIP) || startsWith(head, SIGNATURE_OLE))) return null;
  if (named) return named;
  const text = new TextDecoder('utf-8', { fatal: false }).decode(head.subarray(0, 512)).replace(/^\uFEFF/, '').trimStart().toLowerCase();
  if (text.startsWith('<!doctype html') || text.startsWith('<html')) return 'html';
  return null;
}

/** Which rule an embedded picture fails, or null where the article keeps it. */
export function importPictureProblem(mimeType: string, sizeBytes: number): ImportPictureLeftOut | null {
  if (!isArticlePictureMime(mimeType.toLowerCase())) return 'type';
  if (sizeBytes > ARTICLE_PICTURE_MAX_BYTES) return 'size';
  return null;
}

/** An address a run keeps, or null: a web, mail or telephone address, never a script or an anchor inside the file. */
export function importHref(href: string | null | undefined): string | null {
  const trimmed = (href ?? '').trim();
  return SAFE_HREF.test(trimmed) ? trimmed : null;
}

/** Runs as the article's inline markup, marks nested in one order so two alike runs write alike. */
export function importRunsHtml(runs: readonly ImportRun[]): string {
  return runs
    .map((run) => {
      let html = escapeHtml(run.text).replace(/\n/g, '<br>');
      if (!html) return '';
      if (run.code) html = `<code>${html}</code>`;
      if (run.strike) html = `<strike>${html}</strike>`;
      if (run.underline) html = `<u>${html}</u>`;
      if (run.italic) html = `<i>${html}</i>`;
      if (run.bold) html = `<b>${html}</b>`;
      const href = importHref(run.href);
      if (href) html = `<a href="${escapeHtml(href)}">${html}</a>`;
      return html;
    })
    .join('');
}

/** The words of runs with their marks gone. */
export function importRunsText(runs: readonly ImportRun[]): string {
  return runs.map((run) => run.text).join('').replace(/\s+/g, ' ').trim();
}

/** The sentence a left-out picture leaves in the words, saying where it stood and what it was. */
export function leftOutPictureText(block: { leftOut?: ImportPictureLeftOut; name?: string; alt: string }, words: { lead: string; reason: string }): string {
  const what = (block.name || block.alt || '').trim();
  return what ? `${words.lead} ${what} — ${words.reason}` : `${words.lead} — ${words.reason}`;
}

/** What the body says for a left-out picture, in the four reasons' own words. */
export interface LeftOutWords {
  lead: string;
  reasons: Readonly<Record<ImportPictureLeftOut, string>>;
}

/** Runs with their bold taken off, for a heading and a header cell, which are bold already. */
const unbolded = (runs: readonly ImportRun[]): ImportRun[] => runs.map(({ bold: _bold, ...run }) => run);

function blockHtml(block: ImportBlock, pictureFile: (index: number) => string | null, leftOut: LeftOutWords): string {
  switch (block.kind) {
    case 'heading': {
      const inner = importRunsHtml(unbolded(block.runs));
      return inner ? `<h${block.level}>${inner}</h${block.level}>` : '';
    }
    case 'paragraph': {
      const inner = importRunsHtml(block.runs);
      return inner ? `<p>${inner}</p>` : '';
    }
    case 'quote': {
      const inner = importRunsHtml(block.runs);
      return inner ? `<blockquote>${inner}</blockquote>` : '';
    }
    case 'code':
      return block.text.trim() ? `<pre>${escapeHtml(block.text)}</pre>` : '';
    case 'callout': {
      const inner = importRunsHtml(block.runs);
      return inner ? articleCalloutHtml(block.callout, `<p>${inner}</p>`) : '';
    }
    case 'rule':
      return '<hr>';
    case 'list': {
      const tag = block.ordered ? 'ol' : 'ul';
      const items = block.items.map((item) => `<li>${importRunsHtml(item.runs)}${item.children.map((child) => blockHtml(child, pictureFile, leftOut)).join('')}</li>`).join('');
      // 🔒 A numbered list keeps the number it opens at and a lettered one its letters (decision 1011).
      const attributes = block.ordered ? `${block.style ? ` type="${block.style}"` : ''}${block.start && block.start > 1 ? ` start="${block.start}"` : ''}` : '';
      return items ? `<${tag}${attributes}>${items}</${tag}>` : '';
    }
    case 'table': {
      if (block.rows.length === 0) return '';
      const width = Math.max(...block.rows.map((row) => row.length));
      const cells = (row: ImportRun[][], tag: 'th' | 'td') => Array.from({ length: width }, (_, index) => `<${tag}>${importRunsHtml(tag === 'th' ? unbolded(row[index] ?? []) : (row[index] ?? []))}</${tag}>`).join('');
      const [first, ...rest] = block.rows;
      const head = block.headerRow ? `<thead><tr>${cells(first!, 'th')}</tr></thead>` : '';
      const bodyRows = (block.headerRow ? rest : block.rows).map((row) => `<tr>${cells(row, 'td')}</tr>`).join('');
      return `<table>${head}${bodyRows ? `<tbody>${bodyRows}</tbody>` : ''}</table>`;
    }
    case 'picture': {
      const fileId = block.picture === null ? null : pictureFile(block.picture);
      // 🔒 The picture's own line carries the file's alignment, as the editor's alignment rule writes it, and its caption is the attribute, never the alt (T3571; decision 10511).
      if (fileId) return `<p${block.align ? ` style="text-align: ${block.align};"` : ''}><img data-kb-file="${escapeHtml(fileId)}"${block.alt.trim() ? ` alt="${escapeHtml(block.alt.trim())}"` : ''}${block.caption ? ` ${ARTICLE_PICTURE_CAPTION_ATTRIBUTE}="${escapeHtml(block.caption)}"` : ''}></p>`;
      const reason = leftOut.reasons[block.leftOut ?? 'type'];
      return `<p><i>${escapeHtml(leftOutPictureText(block, { lead: leftOut.lead, reason }))}</i></p>`;
    }
  }
}

/**
 * 🔒 A paragraph opening *Figure*, with or without a number, then a colon, a full stop or a dash, or the number alone
 * (T3571; decision 10511). *Figure 2 shows the wiring* is a sentence and is left a paragraph.
 */
const FIGURE_LINE = /^figure(?:\s+\d+(?:[.-]\d+)*[a-z]?)?\s*(?::|[.\u2013\u2014-](?=\s|$)|$)/i;

/**
 * 🔒 A paragraph directly under a kept picture that opens *Figure* becomes the picture's caption (T3571; decision
 * 10511), cut at the caption's 200 characters and never written into the alt text. A picture left out takes none.
 */
export function importFigureCaptions(blocks: readonly ImportBlock[]): ImportBlock[] {
  const out: ImportBlock[] = [];
  for (const block of blocks) {
    const before = out.at(-1);
    if (block.kind === 'paragraph' && before?.kind === 'picture' && before.picture !== null && !before.caption) {
      const words = importRunsText(block.runs);
      if (FIGURE_LINE.test(words)) {
        out[out.length - 1] = { ...before, caption: words.slice(0, ARTICLE_PICTURE_CAPTION_MAX).trim() };
        continue;
      }
    }
    out.push(block);
  }
  return out;
}

/**
 * 🔒 The stored body an import writes (decision 0953): each block as the
 * article's own markup, a kept picture by its File's id, a left-out one as a
 * line saying so, and the whole run through the article's sanitiser.
 */
export function importedArticleBody(blocks: readonly ImportBlock[], pictureFile: (index: number) => string | null, leftOut: LeftOutWords): string {
  return sanitizeArticleBody(importFigureCaptions(blocks).map((block) => blockHtml(block, pictureFile, leftOut)).join(''));
}

/** The longest title an import writes; a longer one is cut at a word. */
export const IMPORTED_TITLE_MAX = 160;

/** The file's name without its extension, as the title of last resort. */
export function filenameTitle(filename: string): string {
  const base = filename.replace(/^.*[\\/]/, '');
  const dot = base.lastIndexOf('.');
  return (dot > 0 ? base.slice(0, dot) : base).replace(/[_]+/g, ' ').trim();
}

/**
 * The draft's title (piece 30): the title the file names, else its first
 * heading, else the file's name, cut at a word where it runs long.
 */
export function importedTitle(reading: { title: string | null; blocks: readonly ImportBlock[] }, filename: string): string {
  const heading = reading.blocks.find((block): block is Extract<ImportBlock, { kind: 'heading' }> => block.kind === 'heading');
  const chosen = [reading.title ?? '', heading ? importRunsText(heading.runs) : '', filenameTitle(filename)].map((value) => value.replace(/\s+/g, ' ').trim()).find(Boolean) ?? '';
  if (chosen.length <= IMPORTED_TITLE_MAX) return chosen;
  const cut = chosen.slice(0, IMPORTED_TITLE_MAX);
  const space = cut.lastIndexOf(' ');
  return (space > IMPORTED_TITLE_MAX / 2 ? cut.slice(0, space) : cut).trim();
}

/** What a reading holds, counted: what the suites assert and what the window reports. */
export interface ImportCounts {
  headings: number;
  paragraphs: number;
  lists: number;
  listItems: number;
  tables: number;
  pictures: number;
  picturesLeftOut: number;
}

export function importCounts(blocks: readonly ImportBlock[]): ImportCounts {
  const counts: ImportCounts = { headings: 0, paragraphs: 0, lists: 0, listItems: 0, tables: 0, pictures: 0, picturesLeftOut: 0 };
  const visit = (block: ImportBlock) => {
    if (block.kind === 'heading') counts.headings += 1;
    if (block.kind === 'paragraph') counts.paragraphs += 1;
    if (block.kind === 'table') counts.tables += 1;
    if (block.kind === 'picture') {
      if (block.picture === null) counts.picturesLeftOut += 1;
      else counts.pictures += 1;
    }
    if (block.kind === 'list') {
      counts.lists += 1;
      for (const item of block.items) {
        counts.listItems += 1;
        item.children.forEach(visit);
      }
    }
  };
  blocks.forEach(visit);
  return counts;
}

/** Whether blocks carry any words or any picture at all; a reading with none is an empty file. */
export function importHasContent(blocks: readonly ImportBlock[]): boolean {
  const counts = importCounts(blocks);
  if (counts.pictures + counts.picturesLeftOut > 0) return true;
  const words = (block: ImportBlock): boolean => {
    switch (block.kind) {
      case 'heading':
      case 'paragraph':
      case 'quote':
      case 'callout':
        return importRunsText(block.runs) !== '';
      case 'code':
        return block.text.trim() !== '';
      case 'list':
        return block.items.some((item) => importRunsText(item.runs) !== '' || item.children.some(words));
      case 'table':
        return block.rows.some((row) => row.some((cell) => importRunsText(cell) !== ''));
      default:
        return false;
    }
  };
  return blocks.some(words);
}

/** Where the import route answers, relative to the API root (piece 1). */
export const ARTICLE_IMPORT_PATH = '/knowledge/import';

/**
 * 🔒 The six things a reader can be unsure of (T386; decision 1014), each with
 * the tiles it offers, the reader's own pick first: repeated lines at one
 * height, a lettered run under a numbered step, a short styled line, a picture
 * between two steps, a list resuming after it, and Word's tab-aligned pairs.
 */
export const IMPORT_CHOICES = {
  runningLines: ['leave', 'keep', 'each'],
  letteredRun: ['nest', 'own'],
  styledLine: ['heading', 'paragraph'],
  pictureBetween: ['here', 'end'],
  resumedList: ['continue', 'restart'],
  tabPairs: ['table', 'indent'],
  // 🔒 A passage opening Note, Warning, Caution or Tip: a callout, or the paragraph it was (T3572; decision 10530).
  callout: ['callout', 'paragraph'],
} as const;
export type ImportQuestionKind = keyof typeof IMPORT_CHOICES;
export const IMPORT_QUESTION_KINDS = Object.keys(IMPORT_CHOICES) as ImportQuestionKind[];
export type ImportChoice<K extends ImportQuestionKind = ImportQuestionKind> = (typeof IMPORT_CHOICES)[K][number];

/** The answers a person gave, one per kind, holding for the rest of that document; with *Show me each*, the repeated lines kept. */
export type ImportAnswers = { [K in ImportQuestionKind]?: ImportChoice<K> } & { runningLinesKept?: string[] };

/** A line a PDF repeats as a running header or footer: its words as first set, and on how many pages. */
export interface ImportRunningLine {
  text: string;
  pages: number;
}

/** One question the reader asks, about the first passage of its kind. */
export interface ImportQuestion {
  kind: ImportQuestionKind;
  /** The passage asked about, a line each; an empty line is where a picture stands. */
  passage: string[];
  /** The answer given, or the tile the reader would choose, which the window pre-selects. */
  choice: ImportChoice;
  answered: boolean;
  /** The repeated lines, for the running headers and footers. */
  lines?: ImportRunningLine[];
  /** The step a lettered run follows, or the two steps a picture stands between, where the list numbers them. */
  steps?: number[];
  /** The number a resumed list opens at. */
  start?: number;
  /** Asked only where an earlier question was answered so: a list resumes after a picture only where the picture stays. */
  askedIf?: { kind: ImportQuestionKind; choice: ImportChoice };
}

/** Whether a value is one of a kind's tiles. */
export function isImportChoice<K extends ImportQuestionKind>(kind: K, value: unknown): value is ImportChoice<K> {
  return typeof value === 'string' && (IMPORT_CHOICES[kind] as readonly string[]).includes(value);
}

/** 🔒 The answers a post carries, read strictly: an unknown kind or tile is dropped, and the kept lines are words of at most 500 characters. */
export function importAnswersOf(raw: unknown): ImportAnswers {
  const answers: Record<string, unknown> = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return answers as ImportAnswers;
  const given = raw as Record<string, unknown>;
  for (const kind of IMPORT_QUESTION_KINDS) if (isImportChoice(kind, given[kind])) answers[kind] = given[kind];
  if (Array.isArray(given.runningLinesKept)) answers.runningLinesKept = given.runningLinesKept.filter((line): line is string => typeof line === 'string' && line.length <= 500).slice(0, 200);
  return answers as ImportAnswers;
}

/**
 * 🔒 The reader's questions (T386; decision 1014). Each kind is asked once, about
 * its first passage, and its answer holds for the rest of the document; with no
 * answer given, each passage takes the reader's own pick.
 */
export class ImportAsker {
  readonly questions: ImportQuestion[] = [];

  constructor(readonly answers: ImportAnswers = {}) {}

  choose<K extends ImportQuestionKind>(kind: K, pick: ImportChoice<K>, ask: () => Omit<ImportQuestion, 'kind' | 'choice' | 'answered'>): ImportChoice<K> {
    const given = this.answers[kind] as ImportChoice<K> | undefined;
    const choice = given ?? pick;
    if (!this.questions.some((question) => question.kind === kind)) this.questions.push({ kind, choice, answered: given !== undefined, ...ask() });
    return choice;
  }
}

/** One step of the import's window: a question, one repeated line under *Show me each*, or the report before the draft is written. */
export type ImportWizardStep = { kind: 'question'; question: ImportQuestion } | { kind: 'line'; line: ImportRunningLine } | { kind: 'report' };

/** The tiles picked so far: one per kind, and each repeated line kept under *Show me each*. */
export interface ImportWizardPicks {
  choices: Partial<Record<ImportQuestionKind, ImportChoice>>;
  keptLines: readonly string[];
}

/** The tile a question stands on: the one picked, else the reader's own. */
export function importPicked(question: ImportQuestion, picks: ImportWizardPicks): ImportChoice {
  return picks.choices[question.kind] ?? question.choice;
}

/**
 * 🔒 The window's steps (decision 1014): one question at a time, each repeated
 * line in turn under *Show me each*, a question skipped where the answer it
 * hangs on was not given, and the report last.
 */
export function importWizardSteps(questions: readonly ImportQuestion[], picks: ImportWizardPicks): ImportWizardStep[] {
  const steps: ImportWizardStep[] = [];
  for (const question of questions) {
    if (question.askedIf) {
      const hangsOn = questions.find((other) => other.kind === question.askedIf!.kind);
      if (hangsOn && importPicked(hangsOn, picks) !== question.askedIf.choice) continue;
    }
    steps.push({ kind: 'question', question });
    if (question.kind === 'runningLines' && importPicked(question, picks) === 'each') for (const line of question.lines ?? []) steps.push({ kind: 'line', line });
  }
  steps.push({ kind: 'report' });
  return steps;
}

/** The answers the window posts: every question's tile, picked or pre-selected, and the lines kept under *Show me each*. */
export function importWizardAnswers(questions: readonly ImportQuestion[], picks: ImportWizardPicks): ImportAnswers {
  const answers: Record<string, unknown> = {};
  for (const question of questions) answers[question.kind] = importPicked(question, picks);
  if (answers.runningLines === 'each') answers.runningLinesKept = [...picks.keptLines];
  return answers as ImportAnswers;
}

/** How many running lines the answers leave out: every page each repeated line stands on, less those kept. */
export function importLinesLeftOut(questions: readonly ImportQuestion[], picks: ImportWizardPicks): number {
  const running = questions.find((question) => question.kind === 'runningLines');
  if (!running) return 0;
  const choice = importPicked(running, picks);
  if (choice === 'keep') return 0;
  return (running.lines ?? []).filter((line) => choice === 'leave' || !picks.keptLines.includes(line.text)).reduce((sum, line) => sum + line.pages, 0);
}

/** A tile's words: a step's number or a list's start where the question knows it. */
export function importTileLabel(question: Pick<ImportQuestion, 'kind' | 'steps' | 'start'>, choice: ImportChoice): string {
  const steps = question.steps ?? [];
  if (question.kind === 'letteredRun' && choice === 'nest') return steps[0] !== undefined ? fill(t('kb.import.ask.letteredRun.nest'), { n: String(steps[0]) }) : t('kb.import.ask.letteredRun.nestAbove');
  if (question.kind === 'pictureBetween' && choice === 'here') {
    return steps.length === 2 ? fill(t('kb.import.ask.pictureBetween.here'), { a: String(steps[0]), b: String(steps[1]) }) : t('kb.import.ask.pictureBetween.hereOnPage');
  }
  if (question.kind === 'resumedList' && choice === 'continue') return fill(t('kb.import.ask.resumedList.continue'), { n: String(question.start ?? '') });
  return t(`kb.import.ask.${question.kind}.${choice}` as StringKey);
}

/** A question's own sentence. */
export function importQuestionText(question: Pick<ImportQuestion, 'kind' | 'start'>): string {
  if (question.kind === 'resumedList') return fill(t('kb.import.ask.resumedList'), { n: String(question.start ?? '') });
  return t(`kb.import.ask.${question.kind}` as StringKey);
}

/** One question the import asked, and the answer it wrote the draft by. */
export interface ImportAsked {
  kind: ImportQuestionKind;
  choice: ImportChoice;
  answered: boolean;
}

/**
 * 🔒 The import's own report: how many running header and footer lines were
 * left out (T371; decision 1012), and what was asked and answered (T386;
 * decision 1014).
 */
export interface ImportReport {
  linesLeftOut: number;
  asked: ImportAsked[];
}

/** 🔒 What the import route answers where the reader is unsure (decision 1014): its questions, and nothing written. */
export interface ArticleImportQuestions {
  status: 'asking';
  kind: ArticleImportKind;
  title: string;
  questions: ImportQuestion[];
}

/** What the import route answers: the draft written, or the reader's questions. */
export type ArticleImportOutcome = ArticleImportAnswer | ArticleImportQuestions;

/** What the import route answers once it writes: the new draft's id, what it holds, and the import's report. */
export interface ArticleImportAnswer {
  status: 'written';
  id: string;
  title: string;
  kind: ArticleImportKind;
  counts: ImportCounts;
  report: ImportReport;
}

/**
 * 🔒 The words a callout opens on (T3572; decision 10530), in any case: Note,
 * Warning and Tip as themselves, and Caution as Warning, since the article
 * keeps three kinds.
 */
export const IMPORT_CALLOUT_WORDS: Readonly<Record<string, ArticleCalloutKind>> = { note: 'note', warning: 'warning', caution: 'warning', tip: 'tip' };

/** The word, then a colon or a dash; a dash stands apart from the words after it, so *Note-taking* opens nothing. */
const CALLOUT_OPENING = /^\s*(note|warning|caution|tip)\s*(?::|[-\u2013\u2014](?=\s|$))\s*/i;
/** The word set bold on its own, with or without its colon or dash. */
const CALLOUT_WORD_ALONE = /^\s*(note|warning|caution|tip)\s*(?::|[-\u2013\u2014])?\s*$/i;

/** Runs with their first `count` characters cut away and the spaces and line breaks after them. */
function runsAfter(runs: readonly ImportRun[], count: number): ImportRun[] {
  const out: ImportRun[] = [];
  let left = count;
  for (const run of runs) {
    if (left >= run.text.length) {
      left -= run.text.length;
      continue;
    }
    out.push({ ...run, text: run.text.slice(left) });
    left = 0;
  }
  const first = out[0];
  if (first) first.text = first.text.replace(/^[\s\u00a0]+/, '');
  return out.filter((run) => run.text !== '');
}

/**
 * The callout a passage opens as, and the words it holds, or null where it
 * opens on none of the four words (decision 10530): the word followed by a
 * colon or a dash, or set bold apart from the words after it.
 */
export function importCalloutOpening(runs: readonly ImportRun[]): { callout: ArticleCalloutKind; runs: ImportRun[] } | null {
  const text = runs.map((run) => run.text).join('');
  const marked = CALLOUT_OPENING.exec(text);
  if (marked) return { callout: IMPORT_CALLOUT_WORDS[marked[1]!.toLowerCase()]!, runs: runsAfter(runs, marked[0].length) };
  const firstWords = runs.findIndex((run) => run.text.trim() !== '');
  const first = runs[firstWords];
  const alone = first?.bold ? CALLOUT_WORD_ALONE.exec(first.text) : null;
  if (!alone) return null;
  const before = runs.slice(0, firstWords + 1).reduce((sum, run) => sum + run.text.length, 0);
  return { callout: IMPORT_CALLOUT_WORDS[alone[1]!.toLowerCase()]!, runs: runsAfter(runs, before) };
}

/**
 * 🔒 Callouts read from a document's blocks (T3572; decision 10530): a Word
 * paragraph, or a Markdown block quote, opening on Note, Warning, Caution or
 * Tip. A paragraph's callout holds the rest of that paragraph alone, and a word
 * standing alone takes the next paragraph as its words; a quote's holds the rest
 * of the quote. The reader asks once, about the first, and the answer holds for
 * the document; *Keep it as a paragraph* leaves each block as it stood.
 */
export function importCallouts(blocks: readonly ImportBlock[], asker: ImportAsker, from: 'paragraph' | 'quote'): ImportBlock[] {
  const out: ImportBlock[] = [];
  for (let index = 0; index < blocks.length; index++) {
    const block = blocks[index]!;
    const opening = block.kind === from ? importCalloutOpening(block.runs) : null;
    const next = blocks[index + 1];
    // A word standing alone on its line takes the next paragraph as its words, in a Word document alone.
    const borrows = opening !== null && importRunsText(opening.runs) === '' && from === 'paragraph' && next?.kind === 'paragraph';
    const runs = opening && borrows && next?.kind === 'paragraph' ? next.runs : opening?.runs ?? [];
    if (!opening || importRunsText(runs) === '' || block.kind !== from) {
      out.push(block);
      continue;
    }
    const passage = [importRunsText(block.runs), ...(borrows && next?.kind === 'paragraph' ? [importRunsText(next.runs)] : [])];
    if (asker.choose('callout', 'callout', () => ({ passage })) === 'paragraph') {
      out.push(block);
      continue;
    }
    out.push({ kind: 'callout', callout: opening.callout, runs });
    if (borrows) index += 1;
  }
  return out;
}
